<script setup lang="ts">
// "Что тебя волнует?" — three patches cut from the same fabrics as the app's spread
// tiles (/spread), each with her drawing as a paper cut-out sitting on its bottom edge
// (cropped drawings tucked past it). Below the fold: the cut-outs load lazily.
import type { CutoutName } from '@app/utils/paperCutouts';
import PaperCutout from '@app/presentation/components/paper/PaperCutout.vue';
import ScrollReveal from '@/components/ui/animation/ScrollReveal.vue';

interface Concern {
  stock: string;
  fabric: string;
  cutout: CutoutName;
  /** Width of the drawing, % of the patch; `tuck` hides a cropped drawing's flat bottom edge. */
  artWidth: number;
  tuck?: boolean;
  kicker: string;
  t: string;
  s: string;
}

const items: Concern[] = [
  {
    stock: 'bg-stock-rust',
    fabric: 'fabric-gingham',
    cutout: 'love_cat',
    artWidth: 50,
    kicker: 'Отношения',
    t: 'Что он ко мне чувствует?',
    s: 'Самый частый вопрос. Спроси о конкретном человеке — спокойно, без осуждения.',
  },
  {
    stock: 'bg-stock-wine',
    fabric: 'fabric-quilted',
    cutout: 'card_paws',
    artWidth: 52,
    tuck: true,
    kicker: 'Решения',
    t: 'Да или нет?',
    s: 'Экзамен, работа, переезд. Быстрый ответ, когда нужно решиться.',
  },
  {
    stock: 'bg-stock-sage',
    fabric: 'fabric-herringbone',
    cutout: 'rich_cat',
    artWidth: 58,
    tuck: true,
    kicker: 'Деньги и работа',
    t: 'Что с деньгами?',
    s: 'Повышение, переезд, новый проект. Кот подскажет, куда смотреть.',
  },
];
</script>

<template>
  <section
    id="concerns"
    class="scroll-mt-20 py-16 md:py-24"
  >
    <div class="site-container">
      <ScrollReveal class="mb-12 text-center">
        <div class="type-overline mb-3.5 text-primary-ink">
          Зачем сюда приходят
        </div>
        <h2 class="type-display">Что тебя волнует?</h2>
        <p class="mt-3.5 text-[17px] text-muted-foreground">
          Кот разложит карты под любой твой вопрос.
        </p>
      </ScrollReveal>

      <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3">
        <ScrollReveal
          v-for="(it, i) in items"
          :key="i"
          :delay="(i + 1) * 80"
        >
          <article
            class="paper-sheet relative flex h-full min-h-[320px] flex-col overflow-hidden text-foreground"
            :class="[it.stock, it.fabric]"
          >
            <div class="relative z-[1] px-[26px] pt-7">
              <div class="type-overline opacity-85">
                {{ it.kicker }}
              </div>
              <h3 class="type-heading mt-2.5">{{ it.t }}</h3>
              <p class="mt-3 text-[15px] leading-[1.55]">{{ it.s }}</p>
            </div>
            <div
              class="relative z-[1] mt-auto flex min-h-[150px] flex-1 items-end justify-end"
              aria-hidden="true"
            >
              <div
                class="relative"
                :class="it.tuck ? '-mb-[5%] mr-[8%]' : 'mb-3 mr-[6%]'"
                :style="{ width: `${it.artWidth}%` }"
              >
                <PaperCutout
                  :name="it.cutout"
                  lite
                  lazy
                />
              </div>
            </div>
          </article>
        </ScrollReveal>
      </div>
    </div>
  </section>
</template>
